import {useCallback, useState} from "react";
import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import api from "../lib/api";
import {normalizePage, Page} from "../types/page";
import {
    CompetitionResponse,
    CompetitionSetupRequest,
    CompetitorCounts,
    CompetitorDetailResponse,
    CompetitorResponse,
    MatchResponse,
    MatchResultRequest,
    MatchScheduleRequest,
    PlayCategoryRequest,
    PlayCategoryResponse,
    RegistrationConfig,
    RegistrationRequest,
    RegistrationResponse,
    RegistrationStatus,
    ScheduleConflictResponse,
    SchedulingConfigRequest,
    SchedulingConfigResponse,
    StageConfigResponse,
    TournamentResponse,
    TournamentStatus,
} from "../types/tournament";

const base = (businessId: string) => `/api/v2/business/${businessId}/tournaments`;

// Play categories live under a tournament; registration, competitors and the
// competition engine are all scoped to a category.
const catBase = (businessId: string, tournamentId: string) =>
    `${base(businessId)}/${tournamentId}/categories`;

const compBase = (businessId: string, tournamentId: string, categoryId: string) =>
    `${catBase(businessId, tournamentId)}/${categoryId}/competition`;

// Scheduling is its own per-category resource, sibling to /competition.
const scheduleBase = (businessId: string, tournamentId: string, categoryId: string) =>
    `${catBase(businessId, tournamentId)}/${categoryId}/scheduling`;

// ---- Queries ----

// The list endpoint returns a plain array of TournamentResponse (not a Spring page).
async function fetchBusinessTournaments(businessId: string): Promise<TournamentResponse[]> {
    const {data} = await api.get(base(businessId));
    return Array.isArray(data) ? data : (data?.items ?? data?.content ?? []);
}

export function useBusinessTournaments(businessId: string) {
    return useQuery<TournamentResponse[], Error>({
        queryKey: ["business-tournaments", businessId],
        queryFn: () => fetchBusinessTournaments(businessId),
        enabled: !!businessId,
        staleTime: 15_000,
        refetchOnWindowFocus: false,
    });
}

export function useTournament(businessId: string, id: string | null) {
    return useQuery<TournamentResponse, Error>({
        queryKey: ["business-tournament", businessId, id],
        queryFn: async () => {
            const {data} = await api.get(`${base(businessId)}/${id}`);
            return data as TournamentResponse;
        },
        enabled: !!businessId && !!id,
        staleTime: 15_000,
        refetchOnWindowFocus: false,
    });
}

// ---- Play categories ----

const categoriesKey = (businessId: string, tournamentId: string | null) =>
    ["tournament-categories", businessId, tournamentId] as const;

// GET /categories — plain array of PlayCategoryResponse, ordered by position.
export function useCategories(businessId: string, tournamentId: string | null) {
    return useQuery<PlayCategoryResponse[], Error>({
        queryKey: categoriesKey(businessId, tournamentId),
        queryFn: async () => {
            const {data} = await api.get(catBase(businessId, tournamentId!));
            return Array.isArray(data) ? data : (data?.items ?? data?.content ?? []);
        },
        enabled: !!businessId && !!tournamentId,
        staleTime: 15_000,
        refetchOnWindowFocus: false,
    });
}

export function useCategory(
    businessId: string,
    tournamentId: string | null,
    categoryId: string | null
) {
    return useQuery<PlayCategoryResponse, Error>({
        queryKey: ["tournament-category", businessId, tournamentId, categoryId],
        queryFn: async () => {
            const {data} = await api.get(`${catBase(businessId, tournamentId!)}/${categoryId}`);
            return data as PlayCategoryResponse;
        },
        enabled: !!businessId && !!tournamentId && !!categoryId,
        staleTime: 15_000,
        refetchOnWindowFocus: false,
    });
}

export function useCreateCategory(businessId: string, tournamentId: string | null) {
    const qc = useQueryClient();
    return useMutation<PlayCategoryResponse, Error, PlayCategoryRequest>({
        mutationFn: async (req: PlayCategoryRequest) => {
            const {data} = await api.post(catBase(businessId, tournamentId!), req);
            return data as PlayCategoryResponse;
        },
        onSuccess: () => {
            qc.invalidateQueries({queryKey: categoriesKey(businessId, tournamentId)});
        },
    });
}

export function useUpdateCategory(businessId: string, tournamentId: string | null) {
    const qc = useQueryClient();
    return useMutation<PlayCategoryResponse, Error, {categoryId: string; req: PlayCategoryRequest}>({
        mutationFn: async ({categoryId, req}) => {
            const {data} = await api.put(`${catBase(businessId, tournamentId!)}/${categoryId}`, req);
            return data as PlayCategoryResponse;
        },
        onSuccess: (_d, vars) => {
            qc.invalidateQueries({queryKey: categoriesKey(businessId, tournamentId)});
            qc.invalidateQueries({queryKey: ["tournament-category", businessId, tournamentId, vars.categoryId]});
        },
    });
}

export function useDeleteCategory(businessId: string, tournamentId: string | null) {
    const qc = useQueryClient();
    return useMutation<void, Error, string>({
        mutationFn: (categoryId: string) =>
            api.delete(`${catBase(businessId, tournamentId!)}/${categoryId}`).then(() => undefined),
        onSuccess: () => {
            qc.invalidateQueries({queryKey: categoriesKey(businessId, tournamentId)});
        },
    });
}

// ---- Category registration (GET/PUT /categories/{categoryId}/registration) ----

const categoryRegistrationKey = (
    businessId: string,
    tournamentId: string | null,
    categoryId: string | null
) => ["tournament-category-registration", businessId, tournamentId, categoryId] as const;

export function useCategoryRegistration(
    businessId: string,
    tournamentId: string | null,
    categoryId: string | null
) {
    return useQuery<RegistrationConfig, Error>({
        queryKey: categoryRegistrationKey(businessId, tournamentId, categoryId),
        queryFn: async () => {
            const {data} = await api.get(
                `${catBase(businessId, tournamentId!)}/${categoryId}/registration`
            );
            return data as RegistrationConfig;
        },
        enabled: !!businessId && !!tournamentId && !!categoryId,
        staleTime: 10_000,
        refetchOnWindowFocus: false,
    });
}

export function useUpdateCategoryRegistration(
    businessId: string,
    tournamentId: string | null,
    categoryId: string | null
) {
    const qc = useQueryClient();
    return useMutation<RegistrationConfig, Error, RegistrationConfig>({
        mutationFn: async (req: RegistrationConfig) => {
            const {data} = await api.put(
                `${catBase(businessId, tournamentId!)}/${categoryId}/registration`,
                req
            );
            return data as RegistrationConfig;
        },
        onSuccess: (data) => {
            qc.setQueryData(categoryRegistrationKey(businessId, tournamentId, categoryId), data);
            qc.invalidateQueries({queryKey: categoriesKey(businessId, tournamentId)});
        },
    });
}

// ---- Public (no auth required) ----

const publicBase = "/api/v2/public/tournaments";

// Returns a plain array of published TournamentResponse.
export function usePublicTournaments() {
    return useQuery<TournamentResponse[], Error>({
        queryKey: ["public-tournaments"],
        queryFn: async () => {
            const {data} = await api.get(publicBase);
            return Array.isArray(data) ? data : (data?.items ?? data?.content ?? []);
        },
        staleTime: 30_000,
        refetchOnWindowFocus: false,
    });
}

// Public registration — POST /api/v2/public/tournaments/{id}/categories/{categoryId}/register.
// Registration is per play category, so a category must be chosen before registering.
export function useRegisterForCategory(tournamentId: string | null, categoryId: string | null) {
    return useMutation<RegistrationResponse, Error, RegistrationRequest>({
        mutationFn: async (payload: RegistrationRequest) => {
            const {data} = await api.post(
                `${publicBase}/${tournamentId}/categories/${categoryId}/register`,
                payload
            );
            return data as RegistrationResponse;
        },
    });
}

export function usePublicTournament(slug: string | null) {
    return useQuery<TournamentResponse, Error>({
        queryKey: ["public-tournament", slug],
        queryFn: async () => {
            const {data} = await api.get(`${publicBase}/${slug}`);
            return data as TournamentResponse;
        },
        enabled: !!slug,
        staleTime: 30_000,
        refetchOnWindowFocus: false,
    });
}

// Public read-only competition view — per play category:
// GET /api/v2/public/tournaments/{slug}/categories/{categoryId}/competition.
// Returns the same CompetitionResponse shape the business view uses.
export function usePublicCategoryCompetition(slug: string | null, categoryId: string | null) {
    return useQuery<CompetitionResponse, Error>({
        queryKey: ["public-competition", slug, categoryId],
        queryFn: async () => {
            const {data} = await api.get(`${publicBase}/${slug}/categories/${categoryId}/competition`);
            return data as CompetitionResponse;
        },
        enabled: !!slug && !!categoryId,
        staleTime: 30_000,
        refetchOnWindowFocus: false,
    });
}

// ---- User registrations (authenticated) ----
// Mirrors the backend UserTournamentController at /api/v2/users/tournaments.

const userBase = "/api/v2/users/tournaments";

// GET /my-registrations — Spring Page<CompetitorResponse> of the signed-in user's entries.
export function useMyRegistrations(params: {page: number; size: number}) {
    return useQuery<Page<CompetitorResponse>, Error>({
        queryKey: ["my-registrations", params.page, params.size],
        queryFn: async () => {
            const {data} = await api.get(`${userBase}/my-registrations`, {
                params: {page: params.page, size: params.size},
            });
            return normalizePage<CompetitorResponse>(data);
        },
        staleTime: 15_000,
        refetchOnWindowFocus: false,
    });
}

// POST /registrations/{competitorId}/withdraw — returns the updated competitor.
export function useWithdrawRegistration() {
    const qc = useQueryClient();
    return useMutation<CompetitorResponse, Error, string>({
        mutationFn: async (competitorId: string) => {
            const {data} = await api.post(`${userBase}/registrations/${competitorId}/withdraw`);
            return data as CompetitorResponse;
        },
        onSuccess: () => {
            qc.invalidateQueries({queryKey: ["my-registrations"]});
        },
    });
}

// ---- Competitors (scoped to a play category) ----

const competitorsKey = (
    businessId: string,
    tournamentId: string | null,
    categoryId: string | null
) => ["tournament-competitors", businessId, tournamentId, categoryId] as const;

const competitorCountsKey = (businessId: string, tournamentId: string | null) =>
    ["tournament-competitor-counts", businessId, tournamentId] as const;

// GET /{tournamentId}/competitor-counts: per-category and tournament-wide registration
// counts in one call, so the page does not have to page through every category to add up.
export function useCompetitorCounts(businessId: string, tournamentId: string | null) {
    return useQuery<CompetitorCounts, Error>({
        queryKey: competitorCountsKey(businessId, tournamentId),
        queryFn: async () => {
            const {data} = await api.get(`${base(businessId)}/${tournamentId}/competitor-counts`);
            return data as CompetitorCounts;
        },
        enabled: !!businessId && !!tournamentId,
        staleTime: 10_000,
        refetchOnWindowFocus: false,
    });
}

// GET /categories/{categoryId}/competitors — Spring Page<CompetitorResponse>.
export function useCategoryCompetitors(
    businessId: string,
    tournamentId: string | null,
    categoryId: string | null,
    status: RegistrationStatus | undefined,
    params: {page: number; size: number}
) {
    return useQuery<Page<CompetitorResponse>, Error>({
        queryKey: [...competitorsKey(businessId, tournamentId, categoryId), status ?? null, params.page, params.size],
        queryFn: async () => {
            const {data} = await api.get(
                `${catBase(businessId, tournamentId!)}/${categoryId}/competitors`,
                {params: {page: params.page, size: params.size, ...(status ? {status} : {})}}
            );
            return normalizePage<CompetitorResponse>(data, {page: params.page, size: params.size});
        },
        enabled: !!businessId && !!tournamentId && !!categoryId,
        staleTime: 10_000,
        refetchOnWindowFocus: false,
    });
}

// POST /categories/{categoryId}/competitors — organizer adds a competitor directly.
// Admitted immediately regardless of the public open/close window, with no
// entry-fee payment created. Returns the created CompetitorResponse.
export function useRegisterCompetitorForBusiness(
    businessId: string,
    tournamentId: string | null,
    categoryId: string | null
) {
    const qc = useQueryClient();
    return useMutation<CompetitorResponse, Error, RegistrationRequest>({
        mutationFn: async (payload: RegistrationRequest) => {
            const {data} = await api.post(
                `${catBase(businessId, tournamentId!)}/${categoryId}/competitors`,
                payload
            );
            return data as CompetitorResponse;
        },
        onSuccess: () => {
            qc.invalidateQueries({queryKey: competitorsKey(businessId, tournamentId, categoryId)});
            qc.invalidateQueries({queryKey: categoriesKey(businessId, tournamentId)});
            qc.invalidateQueries({queryKey: competitorCountsKey(businessId, tournamentId)});
        },
    });
}

// ---- Mutations ----

export function useDeleteTournament(businessId: string) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (id: string) => api.delete(`${base(businessId)}/${id}`),
        onSuccess: () => {
            qc.invalidateQueries({queryKey: ["business-tournaments", businessId]});
        },
    });
}

export function useTransitionTournamentStatus(businessId: string) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: ({id, target}: {id: string; target: TournamentStatus}) =>
            api.post(`${base(businessId)}/${id}/status`, null, {params: {target}}),
        onSuccess: (_d, vars) => {
            qc.invalidateQueries({queryKey: ["business-tournaments", businessId]});
            qc.invalidateQueries({queryKey: ["business-tournament", businessId, vars.id]});
        },
    });
}

type CompetitorAction = "approve" | "reject" | "waitlist-promote" | "withdraw" | "confirm-payment" | "resend-payment-email";

// Only rails a human may settle by hand. The backend refuses anything else on confirm-payment,
// since nothing there verifies the money arrived and an online payment is settled by its gateway.
export type ManualFacilitator = "CASH" | "EFT" | "INVOICE";

// Competitor actions are addressed by competitor id (business-scoped), but we
// invalidate the active category's competitor list on success.
//
// `facilitator` applies to confirm-payment only, and names the rail the money actually arrived on
// when it differs from the one the entry was filed under: cash taken at the venue against an entry
// that was emailed a card link. Omitting it keeps the entry's existing rail. Supplying it re-prices
// the entry, so a card fee it was carrying comes off.
export function useCompetitorAction(
    businessId: string,
    tournamentId: string | null,
    categoryId: string | null
) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: ({competitorId, action, facilitator}: {
            competitorId: string;
            action: CompetitorAction;
            facilitator?: ManualFacilitator;
        }) =>
            api.post(
                `${base(businessId)}/competitors/${competitorId}/${action}`,
                facilitator ? {facilitator} : undefined
            ),
        onSuccess: () => {
            qc.invalidateQueries({queryKey: competitorsKey(businessId, tournamentId, categoryId)});
            qc.invalidateQueries({queryKey: categoriesKey(businessId, tournamentId)});
            qc.invalidateQueries({queryKey: competitorCountsKey(businessId, tournamentId)});
        },
    });
}

// GET /competitors/{competitorId} — the organiser's full view of one registration
// (contact details and custom-field answers included), used to prefill the edit form.
export function useCompetitor(businessId: string, competitorId: string | null) {
    return useQuery<CompetitorDetailResponse, Error>({
        queryKey: ["tournament-competitor", businessId, competitorId],
        queryFn: async () => {
            const {data} = await api.get(`${base(businessId)}/competitors/${competitorId}`);
            return data as CompetitorDetailResponse;
        },
        enabled: !!businessId && !!competitorId,
        staleTime: 0,
        refetchOnWindowFocus: false,
    });
}

// PUT /competitors/{competitorId} — corrects the registration details. Status, seed and
// payment are untouched by this call.
export function useUpdateCompetitor(
    businessId: string,
    tournamentId: string | null,
    categoryId: string | null
) {
    const qc = useQueryClient();
    return useMutation<CompetitorDetailResponse, Error, {competitorId: string; req: RegistrationRequest}>({
        mutationFn: async ({competitorId, req}) => {
            const {data} = await api.put(`${base(businessId)}/competitors/${competitorId}`, req);
            return data as CompetitorDetailResponse;
        },
        onSuccess: (data, vars) => {
            qc.setQueryData(["tournament-competitor", businessId, vars.competitorId], data);
            qc.invalidateQueries({queryKey: competitorsKey(businessId, tournamentId, categoryId)});
            qc.invalidateQueries({queryKey: categoriesKey(businessId, tournamentId)});
            qc.invalidateQueries({queryKey: competitorCountsKey(businessId, tournamentId)});
        },
    });
}

// PUT /competitors/{competitorId}/category moves a registration into another play
// category of the same tournament. The backend re-evaluates admission against the target
// (accepted when there is room, waitlisted when there is not) and clears the seed. It
// answers 409 once the competitor has been drawn into a competition, and 400 when the
// target takes a different competitor type.
export function useMoveCompetitorCategory(
    businessId: string,
    tournamentId: string | null,
    categoryId: string | null
) {
    const qc = useQueryClient();
    return useMutation<CompetitorDetailResponse, Error, {competitorId: string; targetCategoryId: string}>({
        mutationFn: async ({competitorId, targetCategoryId}) => {
            const {data} = await api.put(
                `${base(businessId)}/competitors/${competitorId}/category`,
                {categoryId: targetCategoryId}
            );
            return data as CompetitorDetailResponse;
        },
        onSuccess: (data, vars) => {
            qc.setQueryData(["tournament-competitor", businessId, vars.competitorId], data);
            // Both the category it left and the one it joined are now stale, as is every
            // other category's list, so the whole tournament's competitor cache is dropped.
            qc.invalidateQueries({queryKey: ["tournament-competitors", businessId, tournamentId]});
            qc.invalidateQueries({queryKey: categoriesKey(businessId, tournamentId)});
            qc.invalidateQueries({queryKey: competitorCountsKey(businessId, tournamentId)});
        },
    });
}

// DELETE /competitors/{competitorId} — removes a withdrawn or rejected registration for good.
// The backend answers 409 for any other status.
export function useDeleteCompetitor(
    businessId: string,
    tournamentId: string | null,
    categoryId: string | null
) {
    const qc = useQueryClient();
    return useMutation<void, Error, string>({
        mutationFn: (competitorId: string) =>
            api.delete(`${base(businessId)}/competitors/${competitorId}`).then(() => undefined),
        onSuccess: () => {
            qc.invalidateQueries({queryKey: competitorsKey(businessId, tournamentId, categoryId)});
            qc.invalidateQueries({queryKey: categoriesKey(businessId, tournamentId)});
            qc.invalidateQueries({queryKey: competitorCountsKey(businessId, tournamentId)});
        },
    });
}

export function useUpdateCompetitorSeed(
    businessId: string,
    tournamentId: string | null,
    categoryId: string | null
) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: ({competitorId, seed}: {competitorId: string; seed: number | null}) =>
            api.put(`${base(businessId)}/competitors/${competitorId}/seed`, {seed}),
        onSuccess: () => {
            qc.invalidateQueries({queryKey: competitorsKey(businessId, tournamentId, categoryId)});
        },
    });
}

// PUT /tournaments/{tournamentId}/seeds — bulk seed update keyed by (globally
// unique) competitor id, so the same endpoint serves every category.
export function useBulkUpdateSeeds(
    businessId: string,
    tournamentId: string | null,
    categoryId: string | null
) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (seeds: Record<string, number>) =>
            api.put(`${base(businessId)}/${tournamentId}/seeds`, {seeds}),
        onSuccess: () => {
            qc.invalidateQueries({queryKey: competitorsKey(businessId, tournamentId, categoryId)});
        },
    });
}

// ---- Competition / bracket engine (scoped to a play category) ----

const competitionKey = (
    businessId: string,
    tournamentId: string | null,
    categoryId: string | null
) => ["tournament-competition", businessId, tournamentId, categoryId] as const;

const stageConfigsKey = (
    businessId: string,
    tournamentId: string | null,
    categoryId: string | null
) => ["tournament-stage-configs", businessId, tournamentId, categoryId] as const;

// Full per-stage configuration (GET /competition/stages). Fetched only when
// editing existing stages, to pre-fill the setup form with stored numeric settings.
export function useStageConfigs(
    businessId: string,
    tournamentId: string | null,
    categoryId: string | null,
    enabled = true
) {
    return useQuery<StageConfigResponse[], Error>({
        queryKey: stageConfigsKey(businessId, tournamentId, categoryId),
        queryFn: async () => {
            const {data} = await api.get(`${compBase(businessId, tournamentId!, categoryId!)}/stages`);
            return Array.isArray(data) ? data : (data?.content ?? data?.items ?? []);
        },
        enabled: enabled && !!businessId && !!tournamentId && !!categoryId,
        staleTime: 10_000,
        refetchOnWindowFocus: false,
    });
}

export function useCompetition(
    businessId: string,
    tournamentId: string | null,
    categoryId: string | null
) {
    return useQuery<CompetitionResponse, Error>({
        queryKey: competitionKey(businessId, tournamentId, categoryId),
        queryFn: async () => {
            const {data} = await api.get(compBase(businessId, tournamentId!, categoryId!));
            return data as CompetitionResponse;
        },
        enabled: !!businessId && !!tournamentId && !!categoryId,
        staleTime: 10_000,
        refetchOnWindowFocus: false,
    });
}

export function useConfigureStages(
    businessId: string,
    tournamentId: string | null,
    categoryId: string | null
) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (req: CompetitionSetupRequest) => {
            const {data} = await api.put(`${compBase(businessId, tournamentId!, categoryId!)}/stages`, req);
            return data as CompetitionResponse;
        },
        onSuccess: (data) => {
            qc.setQueryData(competitionKey(businessId, tournamentId, categoryId), data);
            qc.invalidateQueries({queryKey: stageConfigsKey(businessId, tournamentId, categoryId)});
        },
    });
}

// `includeAwaitingPayment` also draws entrants the organiser has approved whose entry fee has not
// arrived yet, so a draw can be published before every payment clears. Omitted or false draws
// accepted competitors only. Nothing removes an included entrant who then never pays; reset and
// regenerate is how a draw is redone.
export function useGenerateCompetition(
    businessId: string,
    tournamentId: string | null,
    categoryId: string | null
) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (opts?: {includeAwaitingPayment?: boolean}) => {
            const {data} = await api.post(
                `${compBase(businessId, tournamentId!, categoryId!)}/generate`,
                {includeAwaitingPayment: opts?.includeAwaitingPayment ?? false}
            );
            return data as CompetitionResponse;
        },
        onSuccess: (data) => {
            qc.setQueryData(competitionKey(businessId, tournamentId, categoryId), data);
        },
    });
}

export function useResetCompetition(
    businessId: string,
    tournamentId: string | null,
    categoryId: string | null
) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async () => {
            const {data} = await api.post(`${compBase(businessId, tournamentId!, categoryId!)}/reset`);
            return data as CompetitionResponse;
        },
        onSuccess: (data) => {
            qc.setQueryData(competitionKey(businessId, tournamentId, categoryId), data);
        },
    });
}

export function useFinalizeCompetition(
    businessId: string,
    tournamentId: string | null,
    categoryId: string | null
) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: () => api.post(`${compBase(businessId, tournamentId!, categoryId!)}/finalize`),
        onSuccess: () => {
            qc.invalidateQueries({queryKey: competitionKey(businessId, tournamentId, categoryId)});
            qc.invalidateQueries({queryKey: ["business-tournament", businessId, tournamentId]});
            qc.invalidateQueries({queryKey: ["business-tournaments", businessId]});
        },
    });
}

// Publish / unpublish a single stage so it becomes (or stops being) visible on the
// public results view. POST .../competition/stages/{stageId}/publish|unpublish.
export function useSetStagePublished(
    businessId: string,
    tournamentId: string | null,
    categoryId: string | null
) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async ({stageId, published}: {stageId: string; published: boolean}) => {
            const action = published ? "publish" : "unpublish";
            const {data} = await api.post(
                `${compBase(businessId, tournamentId!, categoryId!)}/stages/${stageId}/${action}`
            );
            return data as CompetitionResponse;
        },
        onSuccess: () => {
            qc.invalidateQueries({queryKey: competitionKey(businessId, tournamentId, categoryId)});
            qc.invalidateQueries({queryKey: stageConfigsKey(businessId, tournamentId, categoryId)});
        },
    });
}

// Exchange the places two competitors hold in a generated stage, for an organiser correcting a
// draw by hand. They trade bracket slots, or groups in a group stage, taking each other's fixtures
// and keeping whatever schedule those fixtures already carry. The backend answers 409 once either
// of them has played, since their results would otherwise be handed to somebody else.
export function useSwapCompetitors(
    businessId: string,
    tournamentId: string | null,
    categoryId: string | null
) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async ({stageId, competitorAId, competitorBId}: {
            stageId: string;
            competitorAId: string;
            competitorBId: string;
        }) => {
            const {data} = await api.post(
                `${compBase(businessId, tournamentId!, categoryId!)}/stages/${stageId}/swap`,
                {competitorAId, competitorBId}
            );
            return data as CompetitionResponse;
        },
        onSuccess: (data) => {
            qc.setQueryData(competitionKey(businessId, tournamentId, categoryId), data);
        },
    });
}

// Exchange the places two whole fixtures hold in a generated stage, moving both their competitors
// at once. Each pair arrives in the other's place the way round it was drawn and inherits the time
// and court that place already carried. The backend answers 400 when a fixture still has an unfilled
// place or the two share a competitor, and 409 once any of the four has played.
export function useSwapMatches(
    businessId: string,
    tournamentId: string | null,
    categoryId: string | null
) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async ({stageId, matchAId, matchBId}: {
            stageId: string;
            matchAId: string;
            matchBId: string;
        }) => {
            const {data} = await api.post(
                `${compBase(businessId, tournamentId!, categoryId!)}/stages/${stageId}/swap-matches`,
                {matchAId, matchBId}
            );
            return data as CompetitionResponse;
        },
        onSuccess: (data) => {
            qc.setQueryData(competitionKey(businessId, tournamentId, categoryId), data);
        },
    });
}

export function useRecordMatchResult(
    businessId: string,
    tournamentId: string | null,
    categoryId: string | null
) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async ({matchId, req}: {matchId: string; req: MatchResultRequest}) => {
            const {data} = await api.post(
                `${compBase(businessId, tournamentId!, categoryId!)}/matches/${matchId}/result`,
                req
            );
            return data as MatchResponse;
        },
        onSuccess: () => {
            qc.invalidateQueries({queryKey: competitionKey(businessId, tournamentId, categoryId)});
        },
    });
}

export function useEditMatchResult(
    businessId: string,
    tournamentId: string | null,
    categoryId: string | null
) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async ({matchId, req}: {matchId: string; req: MatchResultRequest}) => {
            const {data} = await api.put(
                `${compBase(businessId, tournamentId!, categoryId!)}/matches/${matchId}/result`,
                req
            );
            return data as MatchResponse;
        },
        onSuccess: () => {
            qc.invalidateQueries({queryKey: competitionKey(businessId, tournamentId, categoryId)});
        },
    });
}

export function useScheduleMatch(
    businessId: string,
    tournamentId: string | null,
    categoryId: string | null
) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async ({matchId, req}: {matchId: string; req: MatchScheduleRequest}) => {
            const {data} = await api.put(
                `${compBase(businessId, tournamentId!, categoryId!)}/matches/${matchId}/schedule`,
                req
            );
            return data as MatchResponse;
        },
        onSuccess: () => {
            qc.invalidateQueries({queryKey: competitionKey(businessId, tournamentId, categoryId)});
        },
    });
}

// ---- Auto-scheduler ----

const scheduleConfigKey = (
    businessId: string,
    tournamentId: string | null,
    categoryId: string | null
) => ["tournament-schedule-config", businessId, tournamentId, categoryId] as const;

const scheduleConflictsKey = (
    businessId: string,
    tournamentId: string | null,
    categoryId: string | null
) => ["tournament-schedule-conflicts", businessId, tournamentId, categoryId] as const;

// GET /scheduling/config — courts + play windows.
export function useScheduleConfig(
    businessId: string,
    tournamentId: string | null,
    categoryId: string | null,
    enabled = true
) {
    return useQuery<SchedulingConfigResponse, Error>({
        queryKey: scheduleConfigKey(businessId, tournamentId, categoryId),
        queryFn: async () => {
            const {data} = await api.get(`${scheduleBase(businessId, tournamentId!, categoryId!)}/config`);
            return data as SchedulingConfigResponse;
        },
        enabled: enabled && !!businessId && !!tournamentId && !!categoryId,
        staleTime: 10_000,
        refetchOnWindowFocus: false,
    });
}

// PUT /scheduling/config — set courts + play windows.
export function useConfigureSchedule(
    businessId: string,
    tournamentId: string | null,
    categoryId: string | null
) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (req: SchedulingConfigRequest) => {
            const {data} = await api.put(
                `${scheduleBase(businessId, tournamentId!, categoryId!)}/config`,
                req
            );
            return data as SchedulingConfigResponse;
        },
        onSuccess: (data) => {
            qc.setQueryData(scheduleConfigKey(businessId, tournamentId, categoryId), data);
        },
    });
}

// POST /scheduling/auto — auto-schedule everything → full competition.
export function useAutoSchedule(
    businessId: string,
    tournamentId: string | null,
    categoryId: string | null
) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async () => {
            const {data} = await api.post(`${scheduleBase(businessId, tournamentId!, categoryId!)}/auto`);
            return data as CompetitionResponse;
        },
        onSuccess: (data) => {
            qc.setQueryData(competitionKey(businessId, tournamentId, categoryId), data);
            qc.invalidateQueries({queryKey: scheduleConflictsKey(businessId, tournamentId, categoryId)});
        },
    });
}

// GET /scheduling/conflicts — clash/gap report.
export function useScheduleConflicts(
    businessId: string,
    tournamentId: string | null,
    categoryId: string | null,
    enabled = true
) {
    return useQuery<ScheduleConflictResponse, Error>({
        queryKey: scheduleConflictsKey(businessId, tournamentId, categoryId),
        queryFn: async () => {
            const {data} = await api.get(`${scheduleBase(businessId, tournamentId!, categoryId!)}/conflicts`);
            return data as ScheduleConflictResponse;
        },
        enabled: enabled && !!businessId && !!tournamentId && !!categoryId,
        staleTime: 5_000,
        refetchOnWindowFocus: false,
    });
}

// ---- PDF exports ----
// Each endpoint streams a PDF with a Content-Disposition filename. We fetch it
// through `api` (so business endpoints carry the Bearer token) and trigger a
// browser download from the returned blob.

// Business per-category exports.
export type CategoryExportKind = "schedule" | "score-sheets" | "bracket" | "standings";
// Tournament-wide (all categories) exports — same kinds as per-category.
export type TournamentExportKind = CategoryExportKind;
// Public exports are limited to schedule + standings.
export type PublicExportKind = "schedule" | "standings";

export const categoryExportUrl = (
    businessId: string,
    tournamentId: string,
    categoryId: string,
    kind: CategoryExportKind
) => `${compBase(businessId, tournamentId, categoryId)}/${kind}.pdf`;

export const tournamentExportUrl = (
    businessId: string,
    tournamentId: string,
    kind: TournamentExportKind
) => `${base(businessId)}/${tournamentId}/export/${kind}.pdf`;

export const publicCategoryExportUrl = (slug: string, categoryId: string, kind: PublicExportKind) =>
    `${publicBase}/${slug}/categories/${categoryId}/competition/${kind}.pdf`;

export const publicTournamentExportUrl = (slug: string, kind: PublicExportKind) =>
    `${publicBase}/${slug}/export/${kind}.pdf`;

// Pull the filename out of `attachment; filename="..."`, falling back to a default.
function filenameFromDisposition(disposition: string | undefined, fallback: string): string {
    if (!disposition) return fallback;
    const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disposition);
    return match?.[1] ? decodeURIComponent(match[1]) : fallback;
}

async function downloadPdf(url: string, fallbackName: string): Promise<void> {
    const res = await api.get(url, {responseType: "blob"});
    const fileName = filenameFromDisposition(
        res.headers?.["content-disposition"] as string | undefined,
        fallbackName
    );
    const blob = new Blob([res.data], {type: "application/pdf"});
    const href = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = href;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(href);
}

// Tracks which export is in-flight (by caller-supplied key) so a single hook can
// drive multiple download buttons with independent loading states.
export function useDownloadPdf() {
    const [pendingKey, setPendingKey] = useState<string | null>(null);
    const download = useCallback(async (key: string, url: string, fallbackName: string) => {
        setPendingKey(key);
        try {
            await downloadPdf(url, fallbackName);
        } finally {
            setPendingKey(null);
        }
    }, []);
    return {pendingKey, download};
}
